/**
 * ai.gmidc 后端（Swagger「上传 · 批量上传图片/视频到 OSS」）
 * 开发环境经 `setupProxy` 的 `/proxy/agiBack` 转发。
 */

import { post } from '../request';
import {
  buildVirtualPortraitAuthHeaders,
  type VirtualPortraitRequestOptions,
} from './virtualPortrait';
import { getEnvBaseUrl } from '../utils/envUtils';

const AGI_BACK_BASE_URL = getEnvBaseUrl(process.env.REACT_APP_AGI_BACK_URL, '/proxy/agiBack');

/** 大图/批量上传耗时较长；覆盖全局默认 30s（见 request defaults）。 */
const UPLOAD_IMAGES_TIMEOUT_MS = 120_000;

export interface BatchUploadImagesToOssResponse {
  code: number;
  message?: string;
  data?: {
    urls?: string[];
  };
}

/** 与图片接口响应结构一致时使用 */
export type BatchUploadVideosToOssResponse = BatchUploadImagesToOssResponse;
export type BatchUploadAudiosToOssResponse = BatchUploadImagesToOssResponse;

/**
 * POST /v1/upload/images — multipart：`files` 为文件数组字段（RFC 惯例为多个同名 part，名称均为 `files`）。
 */
export const batchUploadImagesToOss = async (
  files: File[],
  options: Pick<VirtualPortraitRequestOptions, 'onDebug' | 'signal' | 'systemToken' | 'userId'>,
): Promise<string[]> => {
  if (!files.length) return [];
  const form = new FormData();
  for (const f of files) {
    form.append('files', f, f.name);
  }
  const fullOptions = options as VirtualPortraitRequestOptions;
  const headers = buildVirtualPortraitAuthHeaders(fullOptions);
  options.onDebug?.(`POST ${AGI_BACK_BASE_URL}/v1/upload/images (${files.length} 个文件)`);
  const res = await post<BatchUploadImagesToOssResponse>('/v1/upload/images', form, {
    baseURL: AGI_BACK_BASE_URL,
    headers,
    responseType: 'json',
    timeout: UPLOAD_IMAGES_TIMEOUT_MS,
    ...(options.signal ? { signal: options.signal } : {}),
  });
  if (res.code !== 0) {
    throw new Error(res.message || `批量上传 OSS 失败（code=${res.code}）`);
  }

  const urls = res.data?.urls;
  if (!urls?.length) {
    throw new Error('批量上传 OSS 成功但响应中无 urls');
  }
  if (urls.length !== files.length) {
    throw new Error(`OSS 返回 URL 数（${urls.length}）与上传文件数（${files.length}）不一致`);
  }

  return urls;
};

/**
 * POST /v1/upload/videos — multipart：`files` 为文件数组字段（与批量图片接口相同约定）。
 */
export const batchUploadVideosToOss = async (
  files: File[],
  options: Pick<VirtualPortraitRequestOptions, 'onDebug' | 'signal' | 'systemToken' | 'userId'>,
): Promise<string[]> => {
  if (!files.length) return [];

  const form = new FormData();
  for (const f of files) {
    form.append('files', f, f.name);
  }

  const fullOptions = options as VirtualPortraitRequestOptions;
  const headers = buildVirtualPortraitAuthHeaders(fullOptions);
  options.onDebug?.(`POST ${AGI_BACK_BASE_URL}/v1/upload/videos (${files.length} 个文件)`);

  const res = await post<BatchUploadVideosToOssResponse>('/v1/upload/videos', form, {
    baseURL: AGI_BACK_BASE_URL,
    headers,
    responseType: 'json',
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (res.code !== 0) {
    throw new Error(res.message || `批量上传视频 OSS 失败（code=${res.code}）`);
  }

  const urls = res.data?.urls;
  if (!urls?.length) {
    throw new Error('批量上传视频 OSS 成功但响应中无 urls');
  }
  if (urls.length !== files.length) {
    throw new Error(`OSS 返回 URL 数（${urls.length}）与上传文件数（${files.length}）不一致`);
  }

  return urls;
};

/**
 * POST /v1/upload/audios — multipart：`files` 为文件数组字段（与批量图片/视频接口相同约定）。
 */
export const batchUploadAudiosToOss = async (
  files: File[],
  options: Pick<VirtualPortraitRequestOptions, 'onDebug' | 'signal' | 'systemToken' | 'userId'>,
): Promise<string[]> => {
  if (!files.length) return [];

  const form = new FormData();
  for (const f of files) {
    form.append('files', f, f.name);
  }

  const fullOptions = options as VirtualPortraitRequestOptions;
  const headers = buildVirtualPortraitAuthHeaders(fullOptions);
  options.onDebug?.(`POST ${AGI_BACK_BASE_URL}/v1/upload/audios (${files.length} 个文件)`);

  const res = await post<BatchUploadAudiosToOssResponse>('/v1/upload/audios', form, {
    baseURL: AGI_BACK_BASE_URL,
    headers,
    responseType: 'json',
    timeout: UPLOAD_IMAGES_TIMEOUT_MS,
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (res.code !== 0) {
    throw new Error(res.message || `批量上传音频 OSS 失败（code=${res.code}）`);
  }

  const urls = res.data?.urls;
  if (!urls?.length) {
    throw new Error('批量上传音频 OSS 成功但响应中无 urls');
  }
  if (urls.length !== files.length) {
    throw new Error(`OSS 返回 URL 数（${urls.length}）与上传文件数（${files.length}）不一致`);
  }

  return urls;
};
